import { Navigate, useLocation } from "react-router-dom";
import { userInitialRout } from "./routes/useRoleAccess";
import { getAccessToken } from "./utils/authTokenStore";
import { getUserRolesInLocal } from "./utils/cacheStorage";

interface ProtectRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  protectRoutes: boolean;
  roleGrantAccess?: string[];
}

const ProtectRoute = ({
  children,
  redirectTo = "/auth",
  protectRoutes,
  roleGrantAccess = []
}: ProtectRouteProps): JSX.Element | null => {
  const location = useLocation();
  const allowedRoles = getUserRolesInLocal();

  // Check if user has any of the required roles
  const hasRequiredRole = allowedRoles.some((role: string) => roleGrantAccess.includes(role));

  // If not authenticated, redirect to login
  if (!getAccessToken() && protectRoutes) {
    return <Navigate to={redirectTo} />;
  }

  // If on root path and user doesn't have access to it, redirect to their initial route
  if (location.pathname === "/" && protectRoutes && !hasRequiredRole) {
    const userRole = allowedRoles[0]; // Get user's primary role (e.g., "User (recipe)", "Supplier")

    // Map role value to enum key for lookup
    const roleKeyMap: Record<string, keyof typeof userInitialRout> = {
      "Root Admin": "rootAdmin",
      "Group Admin": "groupAdmin",
      Admin: "admin",
      User: "user",
      Supplier: "supplier",
      "User (recipe)": "sales",
      Sales: "sales"
    };

    const roleKey = roleKeyMap[userRole];
    const initialRoute = roleKey ? userInitialRout[roleKey] : null;

    // Only redirect if initial route is different from root
    if (initialRoute && initialRoute !== "/") {
      return <Navigate to={initialRoute} />;
    }
  }

  // If trying to access protected route without permission, show unauthorized page
  if (location.pathname !== "/" && !hasRequiredRole && protectRoutes) {
    return <Navigate to={"/unauthorized"} />;
  }

  return <>{children}</>;
};

export default ProtectRoute;
